import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import AuthDialog from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-[#FAF8F5]/90 backdrop-blur-xl"
        data-testid="main-header"
      >
        <div className="flex items-stretch">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 font-display font-black text-sm md:text-base tracking-tight px-5 md:px-8 py-3 border-r border-border/60 hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
          >
            <img src="/logo-icon.png" alt="Human Mosaic" className="h-9 w-9 object-contain" data-testid="header-logo-img" />
            <span>HUMANITY MOSAIC</span>
          </Link>
          <nav className="hidden md:flex items-stretch">
            <Link
              to="/grid"
              data-testid="nav-grid-link"
              className="px-6 flex items-center text-xs tracking-[0.2em] text-muted-foreground hover:text-golddark transition-colors duration-200 border-r border-border/60"
            >
              {t("nav.grid").toUpperCase()}
            </Link>
            <a
              href="/#manifesto"
              data-testid="nav-manifesto-link"
              className="px-6 flex items-center text-xs tracking-[0.2em] text-muted-foreground hover:text-golddark transition-colors duration-200 border-r border-border/60"
            >
              {t("nav.manifesto").toUpperCase()}
            </a>
            <Link
              to="/missione"
              data-testid="nav-mission-link"
              className="px-6 flex items-center text-xs tracking-[0.2em] text-muted-foreground hover:text-golddark transition-colors duration-200 border-r border-border/60"
            >
              {t("nav.mission").toUpperCase()}
            </Link>
            <Link
              to="/mosaico"
              data-testid="nav-mosaic-link"
              className="px-6 flex items-center text-xs tracking-[0.2em] text-muted-foreground hover:text-golddark transition-colors duration-200 border-r border-border/60"
            >
              {t("nav.mosaic").toUpperCase()}
            </Link>
            <Link
              to="/stampa"
              data-testid="nav-press-link"
              className="px-6 flex items-center text-xs tracking-[0.2em] text-muted-foreground hover:text-golddark transition-colors duration-200 border-r border-border/60"
            >
              {t("nav.press").toUpperCase()}
            </Link>
          </nav>
          <div className="flex-1" />
          <div className="flex items-stretch border-l border-border/60" data-testid="lang-selector">
            {["it", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}-btn`}
                aria-pressed={lang === l}
                className={`px-4 text-xs tracking-[0.2em] transition-colors duration-200 ${
                  lang === l
                    ? "bg-[#3C3735] text-[#FAF8F5]"
                    : "text-muted-foreground hover:bg-[#3C3735] hover:text-[#FAF8F5]"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-link"
                className="hidden sm:flex px-5 items-center text-xs tracking-[0.2em] text-golddark border-l border-border/60 hover:bg-[#7D8C77] hover:text-[#FAF8F5] transition-colors duration-200"
              >
                {user.name.split(" ")[0].toUpperCase()}
              </Link>
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                data-testid="logout-btn"
                className="px-5 text-xs tracking-[0.2em] text-muted-foreground border-l border-border/60 hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
              >
                {t("nav.logout").toUpperCase()}
              </button>
            </>
          ) : (
            <Button
              onClick={() => setAuthOpen(true)}
              data-testid="login-btn"
              className="rounded-none h-auto px-6 text-xs tracking-[0.2em] bg-[#7D8C77] text-[#FAF8F5] hover:bg-[#3C3735] border-l border-border/60"
            >
              {t("nav.login").toUpperCase()}
            </Button>
          )}
        </div>
      </motion.header>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
